import React, { useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { Link, useParams, useNavigate } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import auth from "../../firebase.init";
import { Helmet } from "react-helmet";

const ItemPayNow = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [user] = useAuthState(auth);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paypalSettings, setPaypalSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);

  useEffect(() => {
    // Fetch order details
    setLoading(true);
    fetch(`https://server.enjoywiki.com/youtubeai-may/item-order/${id}`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(
            `HTTP error! status: ${res.status} - ${res.statusText}`
          );
        }
        return res.json();
      })
      .then((info) => {
        console.log("Order fetched successfully:", info);
        if (!info) {
          setError("Order not found.");
        }
        setOrder(info);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error loading order:", err);
        setError(
          "Failed to load order details. Please check the URL or try again."
        );
        setLoading(false);
      });

    // Fetch PayPal settings
    setSettingsLoading(true);
    fetch(`https://server.enjoywiki.com/youtubeai-may/api/paypal-settings`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(
            `HTTP error! status: ${res.status} - ${res.statusText}`
          );
        }
        return res.json();
      })
      .then((response) => {
        console.log("PayPal settings response:", response);
        if (response.success && response.data) {
          setPaypalSettings(response.data);
        } else {
          console.error("Error in PayPal settings response:", response);
          setError(
            "Failed to load payment gateway settings. Please contact support."
          );
        }
        setSettingsLoading(false);
      })
      .catch((err) => {
        console.error("Error loading PayPal settings:", err);
        setError(
          "Failed to load payment gateway settings. Network issue or server error."
        );
        setSettingsLoading(false);
      });
  }, [id]);

  const createOrder = (data, actions) => {
    if (!order || !paypalSettings) {
      console.error(
        "Order or PayPal settings not available for order creation."
      );
      setError(
        "Payment setup incomplete. Please refresh the page or contact support."
      );
      throw new Error("Missing order or PayPal settings.");
    }

    // Ensure totalPrice is a number and correctly formatted for PayPal
    const totalPrice = parseFloat(order.totalPrice).toFixed(2);
    const packagePrice = parseFloat(order.packagePrice || 0).toFixed(2);
    const processingFee = parseFloat(order.processingFee || 0).toFixed(2);

    console.log(
      "Creating PayPal order with amount:",
      totalPrice,
      "Currency:",
      paypalSettings.currency
    );
    console.log(
      "Order breakdown - Package:",
      packagePrice,
      "Processing Fee:",
      processingFee
    );

    return actions.order.create({
      purchase_units: [
        {
          amount: {
            value: totalPrice,
            currency_code: paypalSettings.currency || "USD",
            breakdown: {
              item_total: {
                currency_code: paypalSettings.currency || "USD",
                value: packagePrice,
              },
              handling: {
                currency_code: paypalSettings.currency || "USD",
                value: processingFee,
              },
            },
          },
          description: order.packageName,
          custom_id: order.orderId,
          invoice_id: order.paymentId,
        },
      ],
    });
  };

  const onApprove = async (data, actions) => {
    try {
      console.log("Payment approval initiated for order ID:", id);
      console.log("PayPal approval data:", data);

      const details = await actions.order.capture();
      console.log("Payment completed details from PayPal:", details);

      // Extract relevant transaction details with better validation
      const capture = details?.purchase_units?.[0]?.payments?.captures?.[0];

      if (!capture || !capture.id) {
        console.error("PayPal capture details missing:", details);
        throw new Error(
          "PayPal capture details are missing from the response."
        );
      }

      // Prepare update data with validation
      const updateData = {
        paypalTransactionID: capture.id,
        payerEmail: details.payer?.email_address || "",
        captureTime: capture.create_time,
        status: "Paid", // This maps to 'paymentStatus' on the server
        orderStatus: "Completed",
        paymentMethod: "Paypal",
      };

      console.log("Sending order status update to server:", updateData);
      console.log(
        "Update URL:",
        `https://server.enjoywiki.com/youtubeai-may/item-update-payment-status/${id}`
      );

      // Optional: Add a verification step before updating (for debugging)
      try {
        const verifyResponse = await fetch(
          `https://server.enjoywiki.com/youtubeai-may/verify-order/${id}`
        );
        if (verifyResponse.ok) {
          const verifyData = await verifyResponse.json();
          console.log("Order verification successful:", verifyData);
        } else {
          console.warn("Order verification failed:", verifyResponse.status);
        }
      } catch (verifyError) {
        console.warn("Order verification request failed:", verifyError);
        // Continue with update anyway
      }

      const response = await fetch(
        `https://server.enjoywiki.com/youtubeai-may/item-update-payment-status/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updateData),
        }
      );

      console.log("Server response status:", response.status);
      console.log(
        "Server response headers:",
        Object.fromEntries(response.headers.entries())
      );

      if (!response.ok) {
        // Try to get error details from server response
        let errorDetails;
        try {
          errorDetails = await response.json();
        } catch (parseError) {
          console.error("Could not parse error response:", parseError);
          errorDetails = {
            error: `HTTP ${response.status}: ${response.statusText}`,
          };
        }

        console.error("Server error details:", errorDetails);
        throw new Error(
          `Failed to update order status: ${
            errorDetails.error || response.statusText
          }`
        );
      }

      const result = await response.json();
      console.log("Server response to update-payment-status:", result);

      // Handle different response scenarios
      if (result.success && result.matchedCount > 0) {
        if (result.modifiedCount > 0) {
          console.log("Order successfully updated in database.");
          setError(null); // Clear any previous errors

          // Show success message briefly before navigation
          setError(
            "Payment successful! Redirecting to your subscription history..."
          );
          setTimeout(() => {
            navigate(`/user/orders-history`);
          }, 2000);
        } else {
          console.warn(
            "Order found but no changes applied (status might already be updated)."
          );
          // Still navigate to success as payment is complete
          setTimeout(() => {
            navigate(`/user/orders-history`);
          }, 1000);
        }
      } else if (result.matchedCount === 0) {
        // Order not found on server
        console.error(
          "Order not found on server for update after successful PayPal payment."
        );
        setError(
          `Payment successful! Transaction ID: ${capture.id}. However, we could not find your order to update. Please contact support with this transaction ID.`
        );

        // Don't navigate away so user can see the error and contact support
        return;
      } else {
        // Other unexpected response
        console.error("Unexpected server response:", result);
        setError(
          "Payment successful, but there was an issue updating your order. Please contact support."
        );
        return;
      }
    } catch (error) {
      console.error("Error during PayPal approval or order update:", error);

      // Provide more specific error messages
      if (error.message.includes("Failed to update order status")) {
        setError(
          `Payment completed successfully, but we couldn't update your order status. Please contact support with your transaction details. Error: ${error.message}`
        );
      } else if (error.message.includes("PayPal capture details")) {
        setError(
          "Payment processing error: Could not retrieve transaction details from PayPal. Please try again."
        );
      } else {
        setError(
          `Payment processing error: ${
            error.message || "An unknown error occurred."
          }`
        );
      }

      // Only navigate away for certain types of errors
      if (
        !error.message.includes("PayPal capture details") &&
        !error.message.includes("contact support")
      ) {
        setTimeout(() => {
          navigate(`/user/dashboard`);
        }, 3000);
      }
    }
  };

  const onCancel = (data) => {
    console.log("Payment cancelled by user:", data);
    setError(
      "Payment was cancelled. You can try again or contact support if you need assistance."
    );
    // Don't navigate immediately, let user see the cancellation message
    setTimeout(() => {
      navigate(`/user/dashboard`);
    }, 3000);
  };

  const onError = (err) => {
    console.error("PayPal button error:", err);
    setError(
      "There was an error with PayPal. Please refresh the page and try again, or contact support if the issue persists."
    );
  };

  // Enhanced error display component
  const renderErrorAlert = () => {
    if (!error) return null;

    const isSuccess =
      error.includes("successful") || error.includes("Redirecting");
    const alertClass = isSuccess ? "alert-success" : "alert-danger";
    const iconClass = isSuccess
      ? "bi-check-circle-fill"
      : "bi-exclamation-triangle-fill";

    return (
      <div className={`alert ${alertClass} mb-4 p-4`}>
        <div className="d-flex align-items-start">
          <i className={`${iconClass} me-3 mt-1`}></i>
          <div>
            <h6 className="mb-2">
              {isSuccess ? "Payment Status" : "Payment Issue"}
            </h6>
            <p className="mb-2">{error}</p>
            {error.includes("contact support") && (
              <div className="mt-3">
                <small className="text-muted">
                  When contacting support, please provide:
                  <ul className="mt-2 mb-0">
                    <li>Order ID: {order?.orderId}</li>
                    <li>PayPal Transaction ID (if payment was successful)</li>
                    <li>Time of transaction: {new Date().toLocaleString()}</li>
                  </ul>
                </small>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading || settingsLoading) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center min-vh-50 py-5">
        <div
          className="spinner-border text-primary"
          role="status"
          style={{ width: "3rem", height: "3rem" }}
        >
          <span className="visually-hidden"></span>
        </div>
        <p className="mt-3 fw-medium text-primary">
          Loading payment details...
        </p>
      </div>
    );
  }

  if (error && (loading || settingsLoading || !order || !paypalSettings)) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6">
            <div className="alert alert-danger shadow-sm border-0 rounded-3 p-4">
              <h4 className="alert-heading mb-3">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>Error
              </h4>
              <p className="mb-3">{error}</p>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-primary px-4 py-2"
                  onClick={() => window.location.reload()}
                >
                  Retry
                </button>
                <button
                  className="btn btn-outline-primary px-4 py-2"
                  onClick={() => navigate("/")}
                >
                  Return to Home
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!order || !paypalSettings) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6">
            <div className="alert alert-warning shadow-sm border-0 rounded-3 p-4">
              <h4 className="alert-heading mb-3">
                <i className="bi bi-question-circle-fill me-2"></i>
                Order/Settings Not Found
              </h4>
              <p className="mb-3">
                We couldn't find the order or necessary payment settings. Please
                check the order ID and try again, or contact support.
              </p>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-primary px-4 py-2"
                  onClick={() => window.location.reload()}
                >
                  Retry
                </button>
                <button
                  className="btn btn-outline-primary px-4 py-2"
                  onClick={() => navigate("/")}
                >
                  Return to Home
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Configure PayPal options based on API response
  const paypalOptions = {
    "client-id": paypalSettings.clientId,
    currency: paypalSettings.currency || "USD",
    intent: "capture",
    components: "buttons",
  };

  // PayPal button styles from API settings
  const buttonStyles = paypalSettings.buttonStyles || {
    layout: "vertical",
    color: "blue",
    shape: "rect",
    label: "pay",
  };

  // If PayPal is disabled in settings, show an appropriate message
  if (paypalSettings.enablePayPal === false) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-8 col-lg-6">
              <div className="alert alert-info shadow-sm border-0 rounded-3 p-4">
                <h4 className="alert-heading mb-3">
                  <i className="bi bi-info-circle-fill me-2"></i>Payment
                  Unavailable
                </h4>
                <p className="mb-3">
                  PayPal payments are currently unavailable. Please contact
                  support for alternative payment methods.
                </p>
                <button
                  className="btn btn-primary px-4 py-2"
                  onClick={() => navigate("/")}
                >
                  Return to Home
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Pay Now | Complete Your Payment to Access Products</title>
        <meta
          name="description"
          content="Complete your payment to unlock your purchased digital products. Secure payment options for a seamless transaction."
        />
      </Helmet>

      <main>
        {/* Breadcrumb Section */}
        <section
          className="position-relative py-4 py-md-5 bg-primary bg-gradient text-white"
          style={{
            background: "linear-gradient(135deg, #4a6bff 0%, #2541b8 100%)",
            overflow: "hidden",
            marginTop: "8rem",
          }}
        >
          <div
            className="position-absolute top-0 end-0 opacity-10"
            style={{
              backgroundImage: "url(assets/img/breadcrumb-bg.svg)",
              backgroundSize: "cover",
              width: "100%",
              height: "100%",
              zIndex: "0",
            }}
          ></div>
          <div className="container position-relative" style={{ zIndex: "1" }}>
            <div className="row">
              <div className="col-lg-8">
                <h2 className="fw-bold mb-2 display-6">
                  Complete Your Payment
                </h2>
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to="/" className="text-white text-opacity-75">
                        Home
                      </Link>
                    </li>
                    <li
                      className="breadcrumb-item active text-white"
                      aria-current="page"
                    >
                      Payment
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </section>

        {/* Payment Details Section */}
        <section className="py-5 bg-light">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-10 col-xl-8">
                <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                  {/* Header */}
                  <div className="card-header bg-white p-4 border-bottom border-light">
                    <div className="d-flex justify-content-between align-items-center flex-wrap">
                      <div>
                        <h4 className="mb-1 fw-bold">
                          {order.packageName || "Digital Product"}
                        </h4>
                        <p className="text-muted mb-0">
                          Order ID:{" "}
                          <span className="fw-medium">{order.orderId}</span>
                        </p>
                      </div>
                      <div className="text-end">
                        <h3 className="mb-0 fw-bold text-primary">
                          ${parseFloat(order.totalPrice).toFixed(2)} 
                        </h3>
                        <span className="badge bg-success rounded-pill px-3 py-2 mt-2">
                          {(order.paymentStatus || "Pending").toUpperCase()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Order Information */}
                  <div className="card-body p-4">
                    <div className="row g-4">
                      {/* Customer Information */}
                      <div className="col-md-6">
                        <div className="p-3 bg-light rounded-3 h-100">
                          <h5 className="mb-3 fw-semibold border-bottom pb-2">
                            Customer Information
                          </h5>
                          <div className="mb-2 d-flex">
                            <span
                              className="text-muted me-2"
                              style={{ width: "100px" }}
                            >
                              Name:
                            </span>
                            <span className="fw-medium">
                              {order.customerName}
                            </span>
                          </div>
                          <div className="mb-2 d-flex">
                            <span
                              className="text-muted me-2"
                              style={{ width: "100px" }}
                            >
                              Email:
                            </span>
                            <span className="fw-medium">
                              {order.customerEmail}
                            </span>
                          </div>
                          <div className="mb-2 d-flex">
                            <span
                              className="text-muted me-2"
                              style={{ width: "100px" }}
                            >
                              Address:
                            </span>
                            <span className="fw-medium">
                              {order.address || "Not specified"}
                            </span>
                          </div>
                          <div className="mb-2 d-flex">
                            <span
                              className="text-muted me-2"
                              style={{ width: "100px" }}
                            >
                              Country:
                            </span>
                            <span className="fw-medium">
                              {order.country || "Not specified"}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Order Details */}
                      <div className="col-md-6">
                        <div className="p-3 bg-light rounded-3 h-100">
                          <h5 className="mb-3 fw-semibold border-bottom pb-2">
                            Order Details
                          </h5>
                          <div className="mb-2 d-flex justify-content-between">
                            <span className="text-muted">Package Price:</span>
                            <span className="fw-medium">
                              ${parseFloat(order.packagePrice || 0).toFixed(2)}{" "}
                              USD
                            </span>
                          </div>
                          {order.processingFee > 0 && (
                            <div className="mb-2 d-flex justify-content-between">
                              <span className="text-muted">
                                Processing Fee:
                              </span>
                              <span className="fw-medium">
                                +${parseFloat(order.processingFee).toFixed(2)}
                              </span>
                            </div>
                          )}
                          {order.Validity && (
                            <div className="mb-2 d-flex justify-content-between">
                              <span className="text-muted">Membership:</span>
                              <span className="fw-medium">
                                {order.Validity} days
                              </span>
                            </div>
                          )}

                          <div className="pt-2 mt-2 border-top border-dashed d-flex justify-content-between">
                            <span className="fw-semibold">Total:</span>
                            <span className="fw-bold text-primary">
                              ${parseFloat(order.totalPrice).toFixed(2)} USD
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Payment Section */}
                    <div className="mt-4 pt-3 border-top">
                      <h5 className="fw-bold mb-4 text-center">
                        Complete Your Payment
                      </h5>

                      {renderErrorAlert()}

                      <div className="row justify-content-center">
                        <div className="col-md-8">
                          <div className="card shadow-sm border-0 mb-4">
                            <div className="card-body p-4">
                              <PayPalScriptProvider options={paypalOptions}>
                                <PayPalButtons
                                  createOrder={createOrder}
                                  onApprove={onApprove}
                                  onCancel={onCancel}
                                  onError={onError}
                                  style={buttonStyles}
                                  disabled={
                                    !!error && !error.includes("successful")
                                  }
                                />
                              </PayPalScriptProvider>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="card-footer bg-white p-4 text-center border-top border-light">
                    <p className="mb-0">
                      Need help? Contact our{" "}
                      <Link to="/contact" className="text-decoration-none">
                        Support Team
                      </Link>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default ItemPayNow;
